<template>
  <DashboardLayout :lastUpdated="lastUpdated">
    <div class="product-details-page">
    <div class="product-details-content">
      <div v-if="!loading && !error && product" class="product-details-back-wrap">
        <button type="button" class="product-details-back-btn" @click="navigateBackToList">
          ← {{ backNavigationLabel }}
        </button>
      </div>
      <div v-if="loading" class="loading-container">
        <div class="ui active loader"></div>
        <p>Loading product details...</p>
      </div>

      <div v-else-if="error" class="error-container">
        <i class="exclamation triangle huge icon"></i>
        <h3>{{ errorTitle }}</h3>
        <p>{{ error }}</p>
        <div class="error-actions">
          <button class="ui primary button" @click="goToAlerts">View All Alerts</button>
          <button class="ui button" @click="goBack">Go Back</button>
        </div>
      </div>

      <div v-else-if="product" class="product-content-grid">
        <div class="grid-full">
          <ProductHeader :product="product" />
        </div>

        <!-- <div class="grid-full action-insights-section">
          <div class="action-panel-teaser">
            <div class="teaser-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"></path>
              </svg>
            </div>
            <div class="teaser-content">
              <div class="teaser-tag">Phase 2 · Coming Soon</div>
              <div class="teaser-title">Action Panel &amp; AI Insights</div>
              <div class="teaser-desc">One-click price adjustments, margin-aware recommendations, and AI-powered Buy Box recovery strategies.</div>
              <div class="teaser-features">
                <div class="teaser-feature"><span class="teaser-feature-dot"></span>Price Adjustment with Margin Guard</div>
                <div class="teaser-feature"><span class="teaser-feature-dot"></span>AI Recovery Recommendations</div>
                <div class="teaser-feature"><span class="teaser-feature-dot"></span>Learning Loop from your decisions</div>
              </div>
            </div>
            <button class="teaser-cta">Join Waitlist →</button>
          </div>
        </div> -->

        <!-- <div v-if="product.status.type !== 'won'" class="grid-full">
          <DiagnosisPanel 
            :diagnosis="product.diagnosis"
          />
        </div> -->

        <div class="grid-full">
          <CurrentOffers 
            :offers="product.currentOffers"
            :marketplace="product.marketplace"
          />
        </div>

        <div class="grid-full">
          <SalesVolumeChart :salesData="product.salesVolume" :marketplace="product.marketplace" />
        </div>

        <div class="grid-full" :class="{ 'two-column': reviewHealthVisible }">
          <ListingSignals :signals="product.listingSignals" />
          <ReviewHealth v-if="reviewHealthVisible" :reviewHealth="product.reviewHealth" />
        </div>

        <div class="grid-full two-column">
          <AccountHealth :accountHealth="product.accountHealth" :isFeaturedMerchant="product.isFeaturedMerchant" />
          <RecentEvents :events="product.recentEvents" @period-changed="handleRecentEventsPeriodChange" />
        </div>
      </div>
    </div>
    </div>
  </DashboardLayout>
</template>

<script>
import './buybox/styles/ProductDetailsPage.css'
import { decodeBackQuery, backLinkLabelForPath } from '../utils/listViewUrlSync.js'
import DashboardLayout from './buybox/DashboardLayout.vue'
import ProductHeader from './product-details/ProductHeader.vue'
import DiagnosisPanel from './product-details/DiagnosisPanel.vue'
import CurrentOffers from './product-details/CurrentOffers.vue'
// import TakeActionPanel from './product-details/TakeActionPanel.vue'
import BuyBoxFactors from './product-details/BuyBoxFactors.vue'
import SalesVolumeChart from './product-details/SalesVolumeChart.vue'
import AccountHealth from './product-details/AccountHealth.vue'
import RecentEvents from './product-details/RecentEvents.vue'
import ListingSignals from './product-details/ListingSignals.vue'
import ReviewHealth from './product-details/ReviewHealth.vue'

export default {
  name: 'ProductDetailsPage',
  components: {
    DashboardLayout,
    ProductHeader,
    DiagnosisPanel,
    CurrentOffers,
    // TakeActionPanel,
    BuyBoxFactors,
    SalesVolumeChart,
    AccountHealth,
    RecentEvents,
    ListingSignals,
    ReviewHealth
  },
  data() {
    return {
      lastUpdated: 'just now',
      lastUpdateTime: null,
      timerInterval: null,
      loading: true,
      error: null,
      errorTitle: 'Error Loading Product',
      product: null,
      activeTab: 'action',
      recentEventsDays: 7 // Default to 7 days
    }
  },
  computed: {
    decodedBackTarget() {
      return decodeBackQuery(this.$route.query.back)
    },
    backNavigationLabel() {
      return backLinkLabelForPath(this.decodedBackTarget)
    },
    /** Review Health card: hidden (no data yet, product/feature < 2 days old) or failed loads render nothing. */
    reviewHealthVisible() {
      var rh = this.product && this.product.reviewHealth
      return !!rh && (rh.state === 'ready' || rh.state === 'waiting')
    }
  },
  methods: {
    navigateBackToList() {
      var target = this.decodedBackTarget
      if (target) {
        this.$router.push(target)
        return
      }
      if (window.history.length > 1) {
        this.$router.go(-1)
      } else {
        this.$router.push('/products')
      }
    },
    goBack() {
      this.navigateBackToList()
    },
    goToAlerts() {
      this.$router.push('/all-alerts')
    },
    async loadProductData() {
      this.loading = true
      this.error = null
      this.errorTitle = 'Error Loading Product'
      
      try {
        const productId = this.$route.query.id
        
        if (!productId) {
          this.errorTitle = 'Product Not Found'
          throw new Error('Please select a product from the All Alerts page to view details.')
        }

        const productResponse = await this.$http.get(`/api/v1/products/${productId}`)
        const productData = productResponse.body

        const apiCalls = [
          this.$http.get(`/api/v1/products/${productId}/sales_volume?days=30`),
          this.$http.get(`/api/v1/products/${productId}/competitor_offers`),
          this.$http.get(`/api/v1/products/${productId}/recent_events?days=${this.recentEventsDays}`),
          this.$http.get(`/api/v1/products/${productId}/account_health`).catch(function(err) {
            console.warn('Failed to load account health:', err)
            return { body: null }
          }),
          this.$http.get(`/api/v1/products/${productId}/review_health`).catch(function(err) {
            console.warn('Failed to load review health:', err)
            return { body: null }
          })
        ]

        const [salesVolumeResponse, competitorOffersResponse, recentEventsResponse, accountHealthResponse, reviewHealthResponse] = await Promise.all(apiCalls)

        const salesVolumeData = salesVolumeResponse.body
        const competitorOffersData = competitorOffersResponse.body
        const recentEventsData = recentEventsResponse.body
        const accountHealthData = accountHealthResponse.body

        this.product = this.transformProductData(
          productData,
          salesVolumeData,
          competitorOffersData,
          recentEventsData,
          accountHealthData,
          reviewHealthResponse.body
        )
        
      } catch (err) {
        if (err.status === 404) {
          this.errorTitle = 'Product Not Found'
          this.error = 'The requested product could not be found.'
        } else if (err.status === 401) {
          this.errorTitle = 'Authentication Required'
          this.error = 'Please log in to view product details.'
        } else {
          this.error = err.message || 'Failed to load product details'
        }
        console.error('Error loading product:', err)
      } finally {
        this.loading = false
      }
    },
    transformProductData(productData, salesVolumeData, competitorOffersData, recentEventsData, accountHealthData, reviewHealthData) {
      return {
        id: (productData.product && productData.product.id) || 0,
        title: (productData.product && productData.product.name) || 'Unknown Product',
        asin: (productData.product && productData.product.asin) || 'N/A',
        sku: (productData.product && productData.product.sku) || null,
        marketplace: (productData.product && productData.product.marketplace.toUpperCase()) || 'US',
        brand: (productData.product && productData.product.brand) || (productData.product && productData.product.seller_name) || productData.seller_name || productData.brand || null,
        imageUrl: (productData.product && productData.product.image_url) || 'https://via.placeholder.com/80',
        status: {
          type: this.mapStatusType(productData.buybox_status && productData.buybox_status.status),
          label: (productData.buybox_status && productData.buybox_status.label) || 'Unknown Status',
          timeAgo: (productData.buybox_status && productData.buybox_status.time_since_change) || 'recently'
        },
        pricing: {
          yourPrice: (productData.your_price && productData.your_price.dollars) || 0,
          buyBoxPrice: (productData.buybox_price && productData.buybox_price.dollars) || 0,
          priceGap: Math.abs((productData.price_gap && productData.price_gap.dollars) || 0),
          estDailyLoss: Math.abs((productData.estimated_daily_loss && productData.estimated_daily_loss.dollars) || 0),
          fbaStock: (productData.fba_stock && productData.fba_stock.quantity) || 0,
          fbaTransferring: (productData.fba_stock && productData.fba_stock.fbaTransferring) != null ? productData.fba_stock.fbaTransferring : 0
        },
        diagnosis: {
          confidence: ((productData.diagnosis && productData.diagnosis.confidence) || 'medium').toLowerCase(),
          primaryCause: (productData.diagnosis && productData.diagnosis.cause) || 'Unknown cause',
          description: (productData.diagnosis && productData.diagnosis.message) || 'No diagnosis available'
        },
        currentOffers: this.transformOffers(competitorOffersData && competitorOffersData.offers),
        listingSignals: this.deriveListingSignals(competitorOffersData && competitorOffersData.offers),
        isFeaturedMerchant: this.deriveIsFeaturedMerchant(competitorOffersData && competitorOffersData.offers),
        buyBoxFactors: this.generateBuyBoxFactors(productData),
        salesVolume: this.transformSalesVolume(salesVolumeData),
        accountHealth: this.transformAccountHealth(accountHealthData),
        recentEvents: this.transformRecentEvents(recentEventsData && recentEventsData.events),
        reviewHealth: reviewHealthData && typeof reviewHealthData === 'object' ? reviewHealthData : null
      }
    },
    transformOffers(offers) {
      if (!offers || !Array.isArray(offers)) return []
      
      return offers.map(offer => {
        const isWinner = offer.is_winner === true || offer.is_winner === 'true'
        const isMyOffer = offer.is_my_offer === true || offer.is_my_offer === 'true'
        
        return {
          seller: offer.seller_name || offer.seller_key || offer.seller_id || 'Unknown',
          sellerId: offer.seller_key || offer.seller_id || offer.seller_name || 'Unknown',
          price: offer.price_dollars || offer.price || 0,
          shipping: offer.shipping_dollars || offer.shipping || 0,
          fulfillment: offer.fulfillment || 'FBM',
          status: offer.status || (isWinner ? 'Has Buy Box' : (isMyOffer ? 'Lost' : 'Not winning')),
          hasBuyBox: isWinner,
          isMyOffer: isMyOffer,
          isPrime: offer.is_prime || false,
          sellerFeedbackCount: offer.seller_feedback_count || null,
          sellerPositiveFeedbackRating: offer.seller_positive_feedback_rating || null,
          isFeaturedMerchant: offer.is_featured_merchant != null ? offer.is_featured_merchant : null,
          isBestSeller: offer.is_best_seller || false
        }
      })
    },
    deriveListingSignals(offers) {
      var myOffer = null
      if (offers && Array.isArray(offers)) {
        for (var i = 0; i < offers.length; i++) {
          if (offers[i].is_my_offer === true || offers[i].is_my_offer === 'true') {
            myOffer = offers[i]
            break
          }
        }
      }
      return [
        { name: 'Best Seller', icon: '🏆', active: myOffer ? !!myOffer.is_best_seller : false },
        { name: 'Prime Eligible', icon: '⚡', active: myOffer ? !!myOffer.is_prime : false }
      ]
    },
    deriveIsFeaturedMerchant(offers) {
      if (!offers || !Array.isArray(offers)) return true
      for (var i = 0; i < offers.length; i++) {
        if (offers[i].is_my_offer === true || offers[i].is_my_offer === 'true') {
          return offers[i].is_featured_merchant !== false
        }
      }
      return false
    },
    generateBuyBoxFactors(productData) {
      const factors = []
      
      // Price factor with safe access
      const priceGapDollars = (productData.price_gap && productData.price_gap.dollars) || 0
      if (priceGapDollars !== 0) {
        factors.push({
          name: 'Price',
          status: priceGapDollars < 0 ? 'issue' : 'ok',
          description: (productData.price_gap && productData.price_gap.formatted) || '$0.00',
          icon: 'dollar sign'
        })
      } else {
        factors.push({
          name: 'Price',
          status: 'ok',
          description: 'Competitive',
          icon: 'dollar sign'
        })
      }
      
      // Fulfillment factor with safe access
      const bothFba = (productData.diagnosis && productData.diagnosis.details && productData.diagnosis.details.both_fba) || false
      factors.push({
        name: 'Fulfillment',
        status: bothFba ? 'ok' : 'warning',
        description: bothFba ? 'Both FBA' : ((productData.diagnosis && productData.diagnosis.details && productData.diagnosis.details.our_fulfillment) || 'Unknown'),
        icon: 'shipping fast'
      })
      
      // Stock factor with safe access
      const isAvailable = (productData.fba_stock && productData.fba_stock.is_available) || false
      factors.push({
        name: 'Stock Level',
        status: isAvailable ? 'ok' : 'issue',
        description: isAvailable ? 'In stock' : 'Out of stock',
        icon: 'boxes'
      })
      
      return factors
    },
    transformSalesVolume(salesVolumeData) {
      // Only use API data, no mock fallbacks
      if (!salesVolumeData) {
        return {
          dailyAverage: 0,
          last7Days: 0,
          last7DaysChange: 0,
          estMonthlyRevenue: 0,
          chartData: []
        }
      }

      var dailyAverage = 0
      if (typeof salesVolumeData.daily_average === 'number') {
        dailyAverage = salesVolumeData.daily_average
      } else if (salesVolumeData.daily_average && salesVolumeData.daily_average.units !== undefined) {
        dailyAverage = salesVolumeData.daily_average.units
      }

      var last7Days = 0
      var last7DaysChange = 0
      if (salesVolumeData.last_7_days) {
        last7Days = salesVolumeData.last_7_days.units || 0
        last7DaysChange = salesVolumeData.last_7_days.percentage_change || 0
      } else if (salesVolumeData.seven_day_change) {
        last7Days = salesVolumeData.seven_day_change.units || 0
        last7DaysChange = salesVolumeData.seven_day_change.percentage || 0
      }

      var estMonthlyRevenue = 0
      if (salesVolumeData.estimated_monthly_revenue) {
        estMonthlyRevenue = salesVolumeData.estimated_monthly_revenue.dollars || 0
      }

      return {
        dailyAverage: dailyAverage,
        last7Days: last7Days,
        last7DaysChange: last7DaysChange,
        estMonthlyRevenue: estMonthlyRevenue,
        chartData: (salesVolumeData.chart_data || []).map(function(item) {
          return {
            date: item.date || item.formatted_date || '',
            units: item.units || 0
          }
        })
      }
    },
    async handleRecentEventsPeriodChange(days) {
      // Update the days filter and reload recent events
      this.recentEventsDays = days
      
      if (!this.product || !this.product.id) {
        return
      }
      
      try {
        const productId = this.product.id
        const recentEventsResponse = await this.$http.get(`/api/v1/products/${productId}/recent_events?days=${days}`)
        const recentEventsData = recentEventsResponse.body
        
        // Update only the recent events in the product object
        if (this.product) {
          this.$set(this.product, 'recentEvents', this.transformRecentEvents(recentEventsData && recentEventsData.events))
        }
      } catch (err) {
        console.error('Error loading recent events:', err)
      }
    },
    transformRecentEvents(events) {
      // Handle missing or malformed data
      if (!events || !Array.isArray(events)) {
        return []
      }

      return events.map(function(event) {
        return {
          type: this.mapEventType(event.type),
          title: event.message || 'Event occurred',
          timeAgo: this.formatTimeAgo(event.timestamp)
        }
      }.bind(this))
    },
    transformAccountHealth(accountHealthData) {
      if (!accountHealthData) {
        return {
          allMetricsHealthy: false,
          metrics: [],
          message: 'Account health data not available'
        }
      }

      var allMetricsHealthy = accountHealthData.status === 'healthy'
      
      var metrics = []
      if (accountHealthData.metrics && Array.isArray(accountHealthData.metrics)) {
        metrics = accountHealthData.metrics.map(function(metric) {
          return {
            name: metric.name || 'Unknown Metric',
            value: metric.value || 'N/A',
            threshold: metric.target || 'N/A', // API uses 'target', component uses 'threshold'
            status: metric.status || 'unknown',
            rawValue: metric.raw_value
          }
        })
      }

      return {
        allMetricsHealthy: allMetricsHealthy,
        metrics: metrics,
        message: accountHealthData.message || (allMetricsHealthy ? 'All Metrics Healthy - No issues affecting Buy Box eligibility' : 'Some metrics need attention'),
        status: accountHealthData.status || 'unknown',
        lastUpdated: accountHealthData.last_updated || null,
        dataSource: accountHealthData.data_source || null
      }
    },
    mapEventType(type) {
      const typeMap = {
        'buybox_lost': 'lost',
        'buybox_won': 'won',
        'competitor_price_drop': 'price-drop',
        'price_change': 'price-change',
        'inventory_change': 'inventory',
        'review_health': 'review-health'
      }
      return typeMap[type] || 'info'
    },
    formatTimeAgo(timestamp) {
      const now = new Date()
      const eventTime = new Date(timestamp)
      const diffMs = now - eventTime
      const diffSec = Math.floor(diffMs / 1000)
      const diffMin = Math.floor(diffSec / 60)
      const diffHr = Math.floor(diffMin / 60)
      const diffDay = Math.floor(diffHr / 24)
      
      if (diffMin < 1) return 'just now'
      if (diffMin < 60) return `${diffMin} minute${diffMin > 1 ? 's' : ''} ago`
      if (diffHr < 24) {
        const remainingMin = diffMin % 60
        return `${diffHr} hour${diffHr > 1 ? 's' : ''} ${remainingMin} minute${remainingMin !== 1 ? 's' : ''} ago`
      }
      return `${diffDay} day${diffDay > 1 ? 's' : ''} ago`
    },
    mapStatusType(status) {
      if (!status) return 'lost'
      const statusLower = status.toLowerCase()
      if (statusLower.includes('suppress')) return 'suppressed'
      if (statusLower.includes('lost')) return 'lost'
      if (statusLower.includes('contest')) return 'contested'
      if (statusLower.includes('won') || statusLower.includes('win')) return 'won'
      return 'lost'
    },
    mapEventType(type) {
      const typeMap = {
        'buybox_lost': 'lost',
        'buybox_won': 'won',
        'competitor_price_drop': 'price-drop',
        'price_change': 'price-change',
        'inventory_change': 'inventory',
        'review_health': 'review-health'
      }
      return typeMap[type] || 'info'
    },
    formatTimeAgo(timestamp) {
      const now = new Date()
      const eventTime = new Date(timestamp)
      const diffMs = now - eventTime
      const diffSec = Math.floor(diffMs / 1000)
      const diffMin = Math.floor(diffSec / 60)
      const diffHr = Math.floor(diffMin / 60)
      const diffDay = Math.floor(diffHr / 24)
      
      if (diffMin < 1) return 'just now'
      if (diffMin < 60) return `${diffMin} minute${diffMin > 1 ? 's' : ''} ago`
      if (diffHr < 24) {
        const remainingMin = diffMin % 60
        return `${diffHr} hour${diffHr > 1 ? 's' : ''} ${remainingMin} minute${remainingMin !== 1 ? 's' : ''} ago`
      }
      return `${diffDay} day${diffDay > 1 ? 's' : ''} ago`
    },
    async handlePriceUpdate(newPrice) {
      try {
        const productId = this.$route.query.id
        
        // TODO: Implement price update API endpoint
        // await this.$http.put(`/api/v1/products/${productId}/price`, { price: newPrice })
        
        await this.loadProductData()
        
        console.log('Price updated successfully to:', newPrice)
        
      } catch (err) {
        console.error('Error updating price:', err)
      }
    },
    updateTimeAgo() {
      if (!this.lastUpdateTime) {
        this.lastUpdated = 'just now'
        return
      }
      
      const now = Date.now()
      const diff = Math.floor((now - this.lastUpdateTime) / 1000)
      
      if (diff < 60) {
        this.lastUpdated = `${diff}s ago`
      } else if (diff < 3600) {
        const minutes = Math.floor(diff / 60)
        this.lastUpdated = `${minutes}m ago`
      } else {
        const hours = Math.floor(diff / 3600)
        this.lastUpdated = `${hours}h ago`
      }
    },
    startTimer() {
      this.lastUpdateTime = Date.now()
      this.lastUpdated = 'just now'
      
      this.timerInterval = setInterval(() => {
        this.updateTimeAgo()
      }, 1000)
    }
  },
  mounted() {
    this.startTimer()
    this.loadProductData()
  },
  beforeDestroy() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval)
    }
  },
  watch: {
    '$route.query.id': {
      handler() {
        this.loadProductData()
      }
    }
  }
}
</script>




// WEBPACK FOOTER //
// ProductDetailsPage.vue?f711e7de